import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { Mail, MessageSquare, Zap, AlertTriangle, Lock, Settings, ExternalLink, Plus, X } from 'lucide-react';
export function NotificationsPage() {
  const navigate = useNavigate();
  // Tab state
  const [activeTab, setActiveTab] = useState<'email' | 'sms'>('email');
  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  // Email tab state
  const [emailProvider, setEmailProvider] = useState('shopify');
  const [klaviyoConnected, setKlaviyoConnected] = useState(false);
  // Customer notifications
  const [subscriptionCreated, setSubscriptionCreated] = useState(true);
  const [subscriptionUpdated, setSubscriptionUpdated] = useState(true);
  const [subscriptionPaused, setSubscriptionPaused] = useState(true);
  const [subscriptionResumed, setSubscriptionResumed] = useState(true);
  const [subscriptionCancelled, setSubscriptionCancelled] = useState(true);
  const [upcomingOrderReminder, setUpcomingOrderReminder] = useState(true);
  const [orderProcessed, setOrderProcessed] = useState(false);
  const [orderShipped, setOrderShipped] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(true);
  const [paymentMethodExpiring, setPaymentMethodExpiring] = useState(true);
  const [paymentRecovered, setPaymentRecovered] = useState(true);
  // Upcoming order reminder settings
  const [reminderDays, setReminderDays] = useState(3);
  const [includeOrderSummary, setIncludeOrderSummary] = useState(true);
  const [includeOrderTotal, setIncludeOrderTotal] = useState(true);
  const [includeEditButton, setIncludeEditButton] = useState(true);
  const [includeSkipButton, setIncludeSkipButton] = useState(true);
  const [includePauseButton, setIncludePauseButton] = useState(false);
  const [includeCancelButton, setIncludeCancelButton] = useState(false);
  // Admin notifications
  const [adminNewSubscription, setAdminNewSubscription] = useState(true);
  const [adminCancellation, setAdminCancellation] = useState(true);
  const [adminPaymentFailure, setAdminPaymentFailure] = useState(true);
  const [adminDailySummary, setAdminDailySummary] = useState(false);
  const [adminWeeklyReport, setAdminWeeklyReport] = useState(false);
  // SMS tab state
  const [smsProvider, setSmsProvider] = useState<'none' | 'klaviyo' | 'postscript'>('none');
  const [smsUpcomingOrder, setSmsUpcomingOrder] = useState(true);
  const [smsUpcomingOrderDays, setSmsUpcomingOrderDays] = useState(3);
  const [smsPaymentFailed, setSmsPaymentFailed] = useState(true);
  const [smsPaymentRecovered, setSmsPaymentRecovered] = useState(false);
  const [smsOrderShipped, setSmsOrderShipped] = useState(false);
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(true);
  const [quietHoursStart, setQuietHoursStart] = useState('9:00 PM');
  const [quietHoursEnd, setQuietHoursEnd] = useState('8:00 AM');
  // Integrations tab state
  const [klaviyoIntegrationConnected, setKlaviyoIntegrationConnected] = useState(true);
  const [webhooksExpanded, setWebhooksExpanded] = useState(false);
  // Klaviyo sync settings
  const [syncSubscriptionStatus, setSyncSubscriptionStatus] = useState(true);
  const [syncStartDate, setSyncStartDate] = useState(true);
  const [syncNextOrderDate, setSyncNextOrderDate] = useState(true);
  const [syncTotalOrders, setSyncTotalOrders] = useState(true);
  const [syncLifetimeValue, setSyncLifetimeValue] = useState(true);
  const [syncCurrentProducts, setSyncCurrentProducts] = useState(true);
  const [syncFrequency, setSyncFrequency] = useState(false);
  const [syncCustomerTags, setSyncCustomerTags] = useState(false);
  const [syncEventCreated, setSyncEventCreated] = useState(true);
  const [syncEventCancelled, setSyncEventCancelled] = useState(true);
  const [syncEventPaused, setSyncEventPaused] = useState(true);
  const [syncEventResumed, setSyncEventResumed] = useState(true);
  const [syncEventOrderCreated, setSyncEventOrderCreated] = useState(true);
  const [syncEventOrderShipped, setSyncEventOrderShipped] = useState(false);
  const [syncEventPaymentFailed, setSyncEventPaymentFailed] = useState(true);
  const [syncEventPaymentRecovered, setSyncEventPaymentRecovered] = useState(true);
  const [syncFrequencyType, setSyncFrequencyType] = useState('realtime');
  const saveSetting = (message: string = 'Settings saved') => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  const timeOptions = ['6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM', '11:00 PM'];
  const customerNotifications = [{
    id: 'created',
    label: 'Subscription Created',
    description: 'Sent when a new subscription is created',
    enabled: subscriptionCreated,
    setEnabled: setSubscriptionCreated
  }, {
    id: 'updated',
    label: 'Subscription Updated',
    description: 'Sent when subscription details change',
    enabled: subscriptionUpdated,
    setEnabled: setSubscriptionUpdated
  }, {
    id: 'paused',
    label: 'Subscription Paused',
    description: 'Sent when a subscription is paused',
    enabled: subscriptionPaused,
    setEnabled: setSubscriptionPaused
  }, {
    id: 'resumed',
    label: 'Subscription Resumed',
    description: 'Sent when a paused subscription resumes',
    enabled: subscriptionResumed,
    setEnabled: setSubscriptionResumed
  }, {
    id: 'cancelled',
    label: 'Subscription Cancelled',
    description: 'Sent when a subscription is cancelled',
    enabled: subscriptionCancelled,
    setEnabled: setSubscriptionCancelled
  }, {
    id: 'reminder',
    label: 'Upcoming Order Reminder',
    description: 'Sent before the next order processes',
    enabled: upcomingOrderReminder,
    setEnabled: setUpcomingOrderReminder
  }, {
    id: 'order-processed',
    label: 'Order Processed',
    description: 'Sent when a subscription order is created',
    enabled: orderProcessed,
    setEnabled: setOrderProcessed
  }, {
    id: 'order-shipped',
    label: 'Order Shipped',
    description: 'Sent when order tracking is available',
    enabled: orderShipped,
    setEnabled: setOrderShipped
  }, {
    id: 'payment-failed',
    label: 'Payment Failed',
    description: 'Sent when a payment attempt fails',
    enabled: paymentFailed,
    setEnabled: setPaymentFailed
  }, {
    id: 'payment-expiring',
    label: 'Payment Method Expiring',
    description: 'Sent when card is about to expire',
    enabled: paymentMethodExpiring,
    setEnabled: setPaymentMethodExpiring
  }, {
    id: 'payment-recovered',
    label: 'Payment Recovered',
    description: 'Sent when a failed payment is recovered',
    enabled: paymentRecovered,
    setEnabled: setPaymentRecovered
  }];
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-7505">
      {/* NavBar */}
      <div className="mb-6" data-id="element-7506">
        <NavBar tabs={[]} title="Notifications" subtitle="Configure email and SMS notifications for subscription events" data-id="element-7507" />
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm mb-6" data-id="element-7508">
        <div className="flex gap-8 px-6" data-id="element-7509">
          <button onClick={() => setActiveTab('email')} className={`h-12 text-[14px] font-medium border-b-2 transition-colors ${activeTab === 'email' ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-7510">
            Email
          </button>
          <button onClick={() => setActiveTab('sms')} className={`h-12 text-[14px] font-medium border-b-2 transition-colors ${activeTab === 'sms' ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-7511">
            SMS
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6" data-id="element-7512">
        {/* EMAIL TAB */}
        {activeTab === 'email' && <>
            {/* Section 1: Email Provider */}
            <Card data-id="element-7513">
              <div className="mb-6" data-id="element-7514">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-7515">
                  Email Provider
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-7516">
                  Choose how subscription emails are sent
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-7517">
                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${emailProvider === 'shopify' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-7518">
                  <input type="radio" name="emailProvider" value="shopify" checked={emailProvider === 'shopify'} onChange={e => {
                setEmailProvider(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-7519" />
                  <div className="flex-1" data-id="element-7520">
                    <div className="flex items-center gap-2 mb-1" data-id="element-7521">
                      <Mail className="w-5 h-5 text-[#0EA5E9]" data-id="element-7522" />
                      <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-7523">
                        Shopify Email
                      </span>
                      <Badge variant="default" data-id="element-7524">Default</Badge>
                    </div>
                    <p className="text-[14px] text-[#64748B]" data-id="element-7525">
                      Use Shopify's built-in email system
                    </p>
                  </div>
                </label>

                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${emailProvider === 'klaviyo' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-7526">
                  <input type="radio" name="emailProvider" value="klaviyo" checked={emailProvider === 'klaviyo'} onChange={e => {
                setEmailProvider(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-7527" />
                  <div className="flex-1" data-id="element-7528">
                    <div className="flex items-center gap-2 mb-1" data-id="element-7529">
                      <Mail className="w-5 h-5 text-[#0EA5E9]" data-id="element-7530" />
                      <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-7531">
                        Klaviyo
                      </span>
                      {klaviyoConnected ? <Badge variant="success" data-id="element-7532">Connected</Badge> : <button className="text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-7533">
                          Connect →
                        </button>}
                    </div>
                    <p className="text-[14px] text-[#64748B]" data-id="element-7534">
                      Send emails through your Klaviyo account
                    </p>
                  </div>
                </label>
              </div>

              {emailProvider === 'klaviyo' && !klaviyoConnected && <div className="mt-4 p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md" data-id="element-7535">
                  <p className="text-[14px] text-[#0369A1] mb-3" data-id="element-7536">
                    You'll be redirected to authorize WinePik
                  </p>
                  <Button variant="primary" size="sm" onClick={() => navigate('/integrations')} data-id="element-7537">
                    Connect Klaviyo
                  </Button>
                </div>}
            </Card>

            {/* Section 2: Customer Email Notifications */}
            <Card data-id="element-7538">
              <div className="mb-6" data-id="element-7539">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-7540">
                  Customer Notifications
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-7541">
                  Emails sent to your subscribers
                </p>
              </div>

              <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-7542">
                {customerNotifications.map((notification, index) => <div key={notification.id} className={`flex items-center justify-between p-4 hover:bg-[#F8FAFC] transition-colors ${index !== customerNotifications.length - 1 ? 'border-b border-[#E2E8F0]' : ''}`} style={{
              minHeight: '64px'
            }} data-id="element-7543">
                    <div className="flex-1" data-id="element-7544">
                      <h3 className="text-[14px] font-semibold text-[#0F172A]" data-id="element-7545">
                        {notification.label}
                      </h3>
                      <p className="text-[13px] text-[#64748B] mt-0.5" data-id="element-7546">
                        {notification.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-7547">
                      <div className="flex items-center gap-2" data-id="element-7548">
                        <button className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-7549">
                          Preview
                        </button>
                        <span className="text-[#CBD5E1]" data-id="element-7550">·</span>
                        <button className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-7551">
                          Edit
                        </button>
                      </div>
                      <button onClick={() => {
                  notification.setEnabled(!notification.enabled);
                  saveSetting();
                }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${notification.enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={notification.enabled} data-id="element-7552">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notification.enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-7553" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </Card>

            {/* Section 3: Upcoming Order Reminder Settings */}
            <Card data-id="element-7554">
              <div className="mb-6" data-id="element-7555">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-7556">
                  Upcoming Order Reminder
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-7557">
                  Configure when to send the upcoming order reminder
                </p>
              </div>

              <div className="space-y-6" data-id="element-7558">
                <div data-id="element-7559">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7560">
                    Send reminder
                  </label>
                  <div className="flex items-center gap-2" data-id="element-7561">
                    <input type="number" value={reminderDays} onChange={e => {
                  setReminderDays(parseInt(e.target.value) || 0);
                  saveSetting();
                }} min="1" max="30" className="w-20 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7562" />
                    <span className="text-[14px] text-[#64748B]" data-id="element-7563">
                      days before order processes
                    </span>
                  </div>
                  <p className="text-[12px] text-[#64748B] mt-1" data-id="element-7564">
                    Customers can make changes before this cutoff
                  </p>
                </div>

                <div data-id="element-7565">
                  <label className="block text-[12px] font-medium text-[#475569] mb-3" data-id="element-7566">
                    Include in reminder
                  </label>
                  <div className="space-y-3" data-id="element-7567">
                    {[{
                  label: 'Order summary with products',
                  enabled: includeOrderSummary,
                  setEnabled: setIncludeOrderSummary
                }, {
                  label: 'Order total',
                  enabled: includeOrderTotal,
                  setEnabled: setIncludeOrderTotal
                }, {
                  label: '"Edit order" button',
                  enabled: includeEditButton,
                  setEnabled: setIncludeEditButton
                }, {
                  label: '"Skip this order" button',
                  enabled: includeSkipButton,
                  setEnabled: setIncludeSkipButton
                }, {
                  label: '"Pause subscription" button',
                  enabled: includePauseButton,
                  setEnabled: setIncludePauseButton
                }, {
                  label: '"Cancel subscription" button',
                  enabled: includeCancelButton,
                  setEnabled: setIncludeCancelButton
                }].map((item, index) => <label key={index} className="flex items-center gap-2 cursor-pointer" data-id="element-7568">
                        <input type="checkbox" checked={item.enabled} onChange={e => {
                    item.setEnabled(e.target.checked);
                    saveSetting();
                  }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-7569" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-7570">
                          {item.label}
                        </span>
                      </label>)}
                  </div>
                </div>
              </div>
            </Card>

            {/* Section 4: Admin Email Notifications */}
            <Card data-id="element-7571">
              <div className="mb-6" data-id="element-7572">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-7573">
                  Admin Notifications
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-7574">
                  Emails sent to store owners and staff
                </p>
              </div>

              <div className="space-y-6" data-id="element-7575">
                {[{
              title: 'New subscription notification',
              description: 'Receive email when a new subscription is created',
              enabled: adminNewSubscription,
              setEnabled: setAdminNewSubscription
            }, {
              title: 'Cancellation notification',
              description: 'Receive email when a subscription is cancelled',
              enabled: adminCancellation,
              setEnabled: setAdminCancellation
            }, {
              title: 'Payment failure notification',
              description: 'Receive email when a payment fails',
              enabled: adminPaymentFailure,
              setEnabled: setAdminPaymentFailure
            }, {
              title: 'Daily summary',
              description: 'Receive a daily email summarizing subscription activity',
              enabled: adminDailySummary,
              setEnabled: setAdminDailySummary
            }, {
              title: 'Weekly report',
              description: 'Receive a weekly report with subscription metrics',
              enabled: adminWeeklyReport,
              setEnabled: setAdminWeeklyReport
            }].map((notification, index) => <div key={index} className="flex items-start justify-between py-4 border-b border-[#E2E8F0] last:border-0" data-id="element-7576">
                    <div className="flex-1 pr-6" data-id="element-7577">
                      <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-7578">
                        {notification.title}
                      </h3>
                      <p className="text-[14px] text-[#64748B]" data-id="element-7579">
                        {notification.description}
                      </p>
                    </div>
                    <button onClick={() => {
                notification.setEnabled(!notification.enabled);
                saveSetting();
              }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${notification.enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={notification.enabled} data-id="element-7580">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notification.enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-7581" />
                    </button>
                  </div>)}

                <div className="pt-6 border-t border-[#E2E8F0]" data-id="element-7582">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7583">
                    Send admin notifications to
                  </label>
                  <div className="flex items-center gap-2 mb-3" data-id="element-7584">
                    <input type="email" placeholder="email@example.com" className="flex-1 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7585" />
                    <Button variant="secondary" size="sm" data-id="element-7586">
                      + Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-7587">
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#E0F2FE] text-[#0369A1] rounded-full text-[13px]" data-id="element-7588">
                      orders@acmewine.com
                      <button className="hover:text-[#EF4444] transition-colors" data-id="element-7589">
                        ×
                      </button>
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </>}

        {/* SMS TAB */}
        {activeTab === 'sms' && <>
            {/* Section 1: SMS Provider */}
            <Card data-id="element-7590">
              <div className="mb-6" data-id="element-7591">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-7592">
                  SMS Provider
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-7593">
                  Connect an SMS service to send text notifications
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-7594">
                <div className="p-4 border border-[#E2E8F0] rounded-lg hover:shadow-md transition-shadow" data-id="element-7595">
                  <div className="flex items-start justify-between mb-3" data-id="element-7596">
                    <div className="flex items-center gap-3" data-id="element-7597">
                      <MessageSquare className="w-8 h-8 text-[#0EA5E9]" data-id="element-7598" />
                      <div data-id="element-7599">
                        <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-7600">
                          Klaviyo SMS
                        </h3>
                        <Badge variant="default" className="mt-1" data-id="element-7601">
                          Not Connected
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <p className="text-[14px] text-[#64748B] mb-4" data-id="element-7602">
                    Send SMS through Klaviyo
                  </p>
                  <Button variant="primary" size="sm" onClick={() => {
                setSmsProvider('klaviyo');
                saveSetting('Klaviyo SMS connected');
              }} data-id="element-7603">
                    Connect
                  </Button>
                </div>

                <div className="p-4 border border-[#E2E8F0] rounded-lg hover:shadow-md transition-shadow" data-id="element-7604">
                  <div className="flex items-start justify-between mb-3" data-id="element-7605">
                    <div className="flex items-center gap-3" data-id="element-7606">
                      <MessageSquare className="w-8 h-8 text-[#0EA5E9]" data-id="element-7607" />
                      <div data-id="element-7608">
                        <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-7609">
                          Postscript
                        </h3>
                        <Badge variant="default" className="mt-1" data-id="element-7610">
                          Not Connected
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <p className="text-[14px] text-[#64748B] mb-4" data-id="element-7611">
                    Send SMS through Postscript
                  </p>
                  <Button variant="primary" size="sm" onClick={() => {
                setSmsProvider('postscript');
                saveSetting('Postscript connected');
              }} data-id="element-7612">
                    Connect
                  </Button>
                </div>
              </div>

              {smsProvider === 'none' && <div className="p-4 bg-[#FEF3C7] border border-[#FDE68A] rounded-md flex items-start gap-3" data-id="element-7613">
                  <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" data-id="element-7614" />
                  <p className="text-[14px] text-[#92400E]" data-id="element-7615">
                    Connect an SMS provider to enable text notifications. SMS
                    can significantly improve payment recovery rates.
                  </p>
                </div>}
            </Card>

            {/* Section 2: SMS Notifications */}
            <Card data-id="element-7616">
              <div className="mb-6" data-id="element-7617">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-7618">
                  SMS Notifications
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-7619">
                  Text messages sent to subscribers who opt in
                </p>
              </div>

              <div className={`space-y-6 ${smsProvider === 'none' ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-7620">
                {smsProvider === 'none' && <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-lg" data-id="element-7621">
                    <p className="text-[16px] font-medium text-[#64748B]" data-id="element-7622">
                      Connect an SMS provider to configure notifications
                    </p>
                  </div>}

                <div className="flex items-start justify-between py-4 border-b border-[#E2E8F0]" data-id="element-7623">
                  <div className="flex-1 pr-6" data-id="element-7624">
                    <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-7625">
                      Upcoming order reminder
                    </h3>
                    <p className="text-[14px] text-[#64748B] mb-2" data-id="element-7626">
                      Text reminder before order processes
                    </p>
                    <div className="flex items-center gap-2" data-id="element-7627">
                      <span className="text-[14px] text-[#64748B]" data-id="element-7628">Send</span>
                      <input type="number" value={smsUpcomingOrderDays} onChange={e => {
                    setSmsUpcomingOrderDays(parseInt(e.target.value) || 0);
                    saveSetting();
                  }} min="1" max="30" className="w-16 h-[32px] px-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7629" />
                      <span className="text-[14px] text-[#64748B]" data-id="element-7630">
                        days before
                      </span>
                    </div>
                  </div>
                  <button onClick={() => {
                setSmsUpcomingOrder(!smsUpcomingOrder);
                saveSetting();
              }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${smsUpcomingOrder ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-7631">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${smsUpcomingOrder ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-7632" />
                  </button>
                </div>

                {[{
              title: 'Payment failed',
              description: 'Alert when payment fails',
              enabled: smsPaymentFailed,
              setEnabled: setSmsPaymentFailed
            }, {
              title: 'Payment recovered',
              description: 'Confirmation when payment is recovered',
              enabled: smsPaymentRecovered,
              setEnabled: setSmsPaymentRecovered
            }, {
              title: 'Order shipped',
              description: 'Notification with tracking info',
              enabled: smsOrderShipped,
              setEnabled: setSmsOrderShipped
            }].map((notification, index) => <div key={index} className="flex items-start justify-between py-4 border-b border-[#E2E8F0] last:border-0" data-id="element-7633">
                    <div className="flex-1 pr-6" data-id="element-7634">
                      <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-7635">
                        {notification.title}
                      </h3>
                      <p className="text-[14px] text-[#64748B]" data-id="element-7636">
                        {notification.description}
                      </p>
                    </div>
                    <button onClick={() => {
                notification.setEnabled(!notification.enabled);
                saveSetting();
              }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${notification.enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-7637">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notification.enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-7638" />
                    </button>
                  </div>)}
              </div>
            </Card>

            {/* Section 3: SMS Compliance */}
            <Card data-id="element-7639">
              <div className="mb-6" data-id="element-7640">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-7641">
                  Compliance Settings
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-7642">
                  Ensure SMS messages meet regulatory requirements
                </p>
              </div>

              <div className={`space-y-6 ${smsProvider === 'none' ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-7643">
                <div className="flex items-start justify-between py-4 border-b border-[#E2E8F0]" data-id="element-7644">
                  <div className="flex-1 pr-6" data-id="element-7645">
                    <div className="flex items-center gap-2 mb-1" data-id="element-7646">
                      <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-7647">
                        Require SMS opt-in
                      </h3>
                      <Lock className="w-4 h-4 text-[#94A3B8]" data-id="element-7648" />
                    </div>
                    <p className="text-[14px] text-[#64748B] mb-1" data-id="element-7649">
                      Customers must explicitly opt in to receive SMS
                    </p>
                    <p className="text-[12px] text-[#94A3B8]" data-id="element-7650">
                      Required by law in most regions
                    </p>
                  </div>
                  <button disabled className="relative inline-flex h-6 w-11 items-center rounded-full bg-[#0EA5E9] opacity-50 cursor-not-allowed flex-shrink-0" data-id="element-7651">
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-6" data-id="element-7652" />
                  </button>
                </div>

                <div className="flex items-start justify-between py-4 border-b border-[#E2E8F0]" data-id="element-7653">
                  <div className="flex-1 pr-6" data-id="element-7654">
                    <div className="flex items-center gap-2 mb-1" data-id="element-7655">
                      <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-7656">
                        Include opt-out instructions
                      </h3>
                      <Lock className="w-4 h-4 text-[#94A3B8]" data-id="element-7657" />
                    </div>
                    <p className="text-[14px] text-[#64748B] mb-1" data-id="element-7658">
                      Every SMS includes 'Reply STOP to unsubscribe'
                    </p>
                    <p className="text-[12px] text-[#94A3B8]" data-id="element-7659">
                      Required by law
                    </p>
                  </div>
                  <button disabled className="relative inline-flex h-6 w-11 items-center rounded-full bg-[#0EA5E9] opacity-50 cursor-not-allowed flex-shrink-0" data-id="element-7660">
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-6" data-id="element-7661" />
                  </button>
                </div>

                <div className="flex items-start justify-between py-4" data-id="element-7662">
                  <div className="flex-1 pr-6" data-id="element-7663">
                    <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-7664">
                      Quiet hours
                    </h3>
                    <p className="text-[14px] text-[#64748B] mb-3" data-id="element-7665">
                      Don't send SMS during nighttime hours
                    </p>
                    {quietHoursEnabled && <div className="flex items-center gap-2 flex-wrap" data-id="element-7666">
                        <span className="text-[14px] text-[#64748B]" data-id="element-7667">
                          No SMS between
                        </span>
                        <select value={quietHoursStart} onChange={e => {
                    setQuietHoursStart(e.target.value);
                    saveSetting();
                  }} className="h-[32px] px-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7668">
                          {timeOptions.map(time => <option key={time} value={time} data-id="element-7669">
                              {time}
                            </option>)}
                        </select>
                        <span className="text-[14px] text-[#64748B]" data-id="element-7670">and</span>
                        <select value={quietHoursEnd} onChange={e => {
                    setQuietHoursEnd(e.target.value);
                    saveSetting();
                  }} className="h-[32px] px-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7671">
                          {timeOptions.map(time => <option key={time} value={time} data-id="element-7672">
                              {time}
                            </option>)}
                        </select>
                        <span className="text-[14px] text-[#64748B]" data-id="element-7673">
                          in customer's local timezone
                        </span>
                      </div>}
                  </div>
                  <button onClick={() => {
                setQuietHoursEnabled(!quietHoursEnabled);
                saveSetting();
              }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${quietHoursEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-7674">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${quietHoursEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-7675" />
                  </button>
                </div>
              </div>
            </Card>
          </>}

        {/* INTEGRATIONS TAB */}
        {activeTab === 'sms' && <>
            {/* Section 1: Email Marketing Platforms */}
            <Card data-id="element-7676">
              <div className="mb-6" data-id="element-7677">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-7678">
                  Email Marketing
                </h2>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-7679">
                  Sync subscription data with your email marketing platform
                </p>
              </div>

              <div className="space-y-4" data-id="element-7680">
                {/* Klaviyo Integration */}
                <div className="border border-[#E2E8F0] rounded-lg p-6 hover:shadow-md transition-shadow" data-id="element-7681">
                  <div className="flex items-start justify-between mb-4" data-id="element-7682">
                    <div className="flex items-center gap-3" data-id="element-7683">
                      <Zap className="w-8 h-8 text-[#0EA5E9]" data-id="element-7684" />
                      <div data-id="element-7685">
                        <h3 className="text-[18px] font-semibold text-[#0F172A]" data-id="element-7686">
                          Klaviyo
                        </h3>
                        <Badge variant="success" className="mt-1" data-id="element-7687">
                          Connected
                        </Badge>
                      </div>
                    </div>
                    <button className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-7688">
                      <Settings className="w-5 h-5 text-[#64748B]" data-id="element-7689" />
                    </button>
                  </div>

                  <p className="text-[14px] text-[#64748B] mb-4" data-id="element-7690">
                    Sync subscriber data and trigger flows based on subscription
                    events. Perfect for winback campaigns and lifecycle emails.
                  </p>

                  <p className="text-[13px] text-[#94A3B8] mb-4" data-id="element-7691">
                    Last synced: 5 minutes ago
                  </p>

                  <div className="flex items-center gap-3" data-id="element-7692">
                    <Button variant="secondary" size="sm" data-id="element-7693">
                      Sync Now
                    </Button>
                    <Button variant="ghost" size="sm" data-id="element-7694">
                      View Sync Settings
                    </Button>
                    <Button variant="ghost" size="sm" className="text-[#EF4444]" data-id="element-7695">
                      Disconnect
                    </Button>
                  </div>
                </div>

                {/* Mailchimp - Coming Soon */}
                <div className="border border-[#E2E8F0] rounded-lg p-6 opacity-60" data-id="element-7696">
                  <div className="flex items-start justify-between mb-4" data-id="element-7697">
                    <div className="flex items-center gap-3" data-id="element-7698">
                      <Mail className="w-8 h-8 text-[#64748B]" data-id="element-7699" />
                      <div data-id="element-7700">
                        <h3 className="text-[18px] font-semibold text-[#0F172A]" data-id="element-7701">
                          Mailchimp
                        </h3>
                        <Badge variant="default" className="mt-1 bg-[#EDE9FE] text-[#6F42C1]" data-id="element-7702">
                          Coming Soon 🔒
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <p className="text-[14px] text-[#64748B] mb-4" data-id="element-7703">
                    Sync subscribers to Mailchimp audiences and trigger
                    automations
                  </p>

                  <Button variant="secondary" size="sm" disabled data-id="element-7704">
                    Join Waitlist
                  </Button>
                </div>

                {/* Drip - Coming Soon */}
                <div className="border border-[#E2E8F0] rounded-lg p-6 opacity-60" data-id="element-7705">
                  <div className="flex items-start justify-between mb-4" data-id="element-7706">
                    <div className="flex items-center gap-3" data-id="element-7707">
                      <Mail className="w-8 h-8 text-[#64748B]" data-id="element-7708" />
                      <div data-id="element-7709">
                        <h3 className="text-[18px] font-semibold text-[#0F172A]" data-id="element-7710">
                          Drip
                        </h3>
                        <Badge variant="default" className="mt-1 bg-[#EDE9FE] text-[#6F42C1]" data-id="element-7711">
                          Coming Soon 🔒
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <p className="text-[14px] text-[#64748B] mb-4" data-id="element-7712">
                    Connect with Drip for advanced ecommerce automation
                  </p>

                  <Button variant="secondary" size="sm" disabled data-id="element-7713">
                    Join Waitlist
                  </Button>
                </div>
              </div>
            </Card>

            {/* Section 2: Klaviyo Sync Settings */}
            {klaviyoIntegrationConnected && <Card data-id="element-7714">
                <div className="mb-6" data-id="element-7715">
                  <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-7716">
                    Klaviyo Sync Settings
                  </h2>
                  <p className="text-[14px] text-[#64748B] mt-1" data-id="element-7717">
                    Configure what data syncs to Klaviyo
                  </p>
                </div>

                <div className="space-y-6" data-id="element-7718">
                  <div data-id="element-7719">
                    <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-3" data-id="element-7720">
                      Subscriber Properties
                    </h3>
                    <div className="space-y-2" data-id="element-7721">
                      {[{
                  label: 'Subscription status (active, paused, cancelled)',
                  enabled: syncSubscriptionStatus,
                  setEnabled: setSyncSubscriptionStatus
                }, {
                  label: 'Subscription start date',
                  enabled: syncStartDate,
                  setEnabled: setSyncStartDate
                }, {
                  label: 'Next order date',
                  enabled: syncNextOrderDate,
                  setEnabled: setSyncNextOrderDate
                }, {
                  label: 'Total subscription orders',
                  enabled: syncTotalOrders,
                  setEnabled: setSyncTotalOrders
                }, {
                  label: 'Lifetime subscription value',
                  enabled: syncLifetimeValue,
                  setEnabled: setSyncLifetimeValue
                }, {
                  label: 'Current subscription products',
                  enabled: syncCurrentProducts,
                  setEnabled: setSyncCurrentProducts
                }, {
                  label: 'Subscription frequency',
                  enabled: syncFrequency,
                  setEnabled: setSyncFrequency
                }, {
                  label: 'Customer tags',
                  enabled: syncCustomerTags,
                  setEnabled: setSyncCustomerTags
                }].map((item, index) => <label key={index} className="flex items-center gap-2 cursor-pointer" data-id="element-7722">
                          <input type="checkbox" checked={item.enabled} onChange={e => {
                    item.setEnabled(e.target.checked);
                    saveSetting();
                  }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-7723" />
                          <span className="text-[14px] text-[#0F172A]" data-id="element-7724">
                            {item.label}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-7725">
                    <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-3" data-id="element-7726">
                      Events to Sync
                    </h3>
                    <div className="space-y-2" data-id="element-7727">
                      {[{
                  label: 'Subscription Created',
                  enabled: syncEventCreated,
                  setEnabled: setSyncEventCreated
                }, {
                  label: 'Subscription Cancelled',
                  enabled: syncEventCancelled,
                  setEnabled: setSyncEventCancelled
                }, {
                  label: 'Subscription Paused',
                  enabled: syncEventPaused,
                  setEnabled: setSyncEventPaused
                }, {
                  label: 'Subscription Resumed',
                  enabled: syncEventResumed,
                  setEnabled: setSyncEventResumed
                }, {
                  label: 'Order Created',
                  enabled: syncEventOrderCreated,
                  setEnabled: setSyncEventOrderCreated
                }, {
                  label: 'Order Shipped',
                  enabled: syncEventOrderShipped,
                  setEnabled: setSyncEventOrderShipped
                }, {
                  label: 'Payment Failed',
                  enabled: syncEventPaymentFailed,
                  setEnabled: setSyncEventPaymentFailed
                }, {
                  label: 'Payment Recovered',
                  enabled: syncEventPaymentRecovered,
                  setEnabled: setSyncEventPaymentRecovered
                }].map((item, index) => <label key={index} className="flex items-center gap-2 cursor-pointer" data-id="element-7728">
                          <input type="checkbox" checked={item.enabled} onChange={e => {
                    item.setEnabled(e.target.checked);
                    saveSetting();
                  }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-7729" />
                          <span className="text-[14px] text-[#0F172A]" data-id="element-7730">
                            {item.label}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-7731">
                    <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-3" data-id="element-7732">
                      Sync Frequency
                    </h3>
                    <div className="space-y-2" data-id="element-7733">
                      {[{
                  value: 'realtime',
                  label: 'Real-time (via webhooks)'
                }, {
                  value: '15min',
                  label: 'Every 15 minutes'
                }, {
                  value: 'hourly',
                  label: 'Every hour'
                }, {
                  value: 'daily',
                  label: 'Daily'
                }].map(option => <label key={option.value} className="flex items-center gap-2 cursor-pointer" data-id="element-7734">
                          <input type="radio" name="syncFrequency" value={option.value} checked={syncFrequencyType === option.value} onChange={e => {
                    setSyncFrequencyType(e.target.value);
                    saveSetting();
                  }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-7735" />
                          <span className="text-[14px] text-[#0F172A]" data-id="element-7736">
                            {option.label}
                          </span>
                        </label>)}
                    </div>
                  </div>
                </div>
              </Card>}

            {/* Section 3: Webhooks */}
            <Card data-id="element-7737">
              <div className="flex items-center justify-between cursor-pointer" onClick={() => setWebhooksExpanded(!webhooksExpanded)} data-id="element-7738">
                <div data-id="element-7739">
                  <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-7740">
                    Webhooks
                  </h2>
                  <p className="text-[14px] text-[#64748B] mt-1" data-id="element-7741">
                    Send subscription events to external systems
                  </p>
                </div>
                <button className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-7742">
                  {webhooksExpanded ? <X className="w-5 h-5 text-[#64748B]" data-id="element-7743" /> : <Plus className="w-5 h-5 text-[#64748B]" data-id="element-7744" />}
                </button>
              </div>

              {webhooksExpanded && <div className="mt-6 pt-6 border-t border-[#E2E8F0]" data-id="element-7745">
                  <div className="border border-[#E2E8F0] rounded-lg overflow-hidden mb-4" data-id="element-7746">
                    <table className="w-full" data-id="element-7747">
                      <thead className="bg-[#F1F5F9]" data-id="element-7748">
                        <tr data-id="element-7749">
                          <th className="px-4 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-7750">
                            Endpoint URL
                          </th>
                          <th className="px-4 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-7751">
                            Events
                          </th>
                          <th className="px-4 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-7752">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-7753">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E2E8F0]" data-id="element-7754">
                        <tr className="hover:bg-[#F8FAFC]" data-id="element-7755">
                          <td className="px-4 py-3 text-[14px] text-[#0F172A]" data-id="element-7756">
                            https://api.example.com/webhooks
                          </td>
                          <td className="px-4 py-3 text-[14px] text-[#64748B]" data-id="element-7757">
                            All events
                          </td>
                          <td className="px-4 py-3" data-id="element-7758">
                            <Badge variant="success" data-id="element-7759">Active</Badge>
                          </td>
                          <td className="px-4 py-3" data-id="element-7760">
                            <div className="flex items-center gap-2" data-id="element-7761">
                              <button className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-7762">
                                Edit
                              </button>
                              <span className="text-[#CBD5E1]" data-id="element-7763">·</span>
                              <button className="text-[14px] font-medium text-[#EF4444] hover:text-[#DC2626]" data-id="element-7764">
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                        <tr className="hover:bg-[#F8FAFC]" data-id="element-7765">
                          <td className="px-4 py-3 text-[14px] text-[#0F172A]" data-id="element-7766">
                            https://zapier.com/hooks/catch/123
                          </td>
                          <td className="px-4 py-3 text-[14px] text-[#64748B]" data-id="element-7767">
                            Cancellations only
                          </td>
                          <td className="px-4 py-3" data-id="element-7768">
                            <Badge variant="success" data-id="element-7769">Active</Badge>
                          </td>
                          <td className="px-4 py-3" data-id="element-7770">
                            <div className="flex items-center gap-2" data-id="element-7771">
                              <button className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-7772">
                                Edit
                              </button>
                              <span className="text-[#CBD5E1]" data-id="element-7773">·</span>
                              <button className="text-[14px] font-medium text-[#EF4444] hover:text-[#DC2626]" data-id="element-7774">
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <Button variant="secondary" size="sm" iconLeft={<Plus className="w-4 h-4" data-id="element-7776" />} className="border-dashed" data-id="element-7775">
                    Add Webhook
                  </Button>
                </div>}
            </Card>
          </>}
      </div>

      {/* Toast Notification */}
      {showToast && <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2 duration-200" data-id="element-7777">
          <div className="bg-white border border-[#CBD5E1] rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[320px]" data-id="element-7778">
            <div className="w-1 h-10 bg-[#10B981] rounded-full" data-id="element-7779" />
            <div className="flex-1" data-id="element-7780">
              <p className="text-[14px] font-semibold text-[#0F172A]" data-id="element-7781">
                Settings saved
              </p>
              <p className="text-[13px] text-[#64748B]" data-id="element-7782">{toastMessage}</p>
            </div>
          </div>
        </div>}
    </div>;
}